import React, { useMemo } from "react";
import { useStore } from "../../../helpers";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import CellField, { AssignedBadge } from "./CellField";
import { IconButton } from "@mui/material";
import { CircularProgress } from "./CircularProgress";
import { safe_get } from "../../../report-visuals";
import { toast } from "react-toastify";

const Columns = () => {
  const rootStore = useStore();
  const { taskManagerStore } = rootStore;
  const {
    task_filters: { userList, status, pivLevel1 },
    tasklist_current_page_number,
  } = taskManagerStore;
  const params = useParams();
  const navigate = useNavigate();
  const projectid = params.projectid;

  const actionButtons = (params: any) => {
    const taskId = params.data?.id;

    const refreshTaskList = async () => {
      // Invalidate cache and refresh task list
      await taskManagerStore.get_task_list(projectid);
    };

    const handleClone = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!taskId) return;

      try {
        const res = await taskManagerStore.clone_task(taskId);
        if (res?.status === 200 || res?.status === 204) {
          toast.success("Task cloned successfully");
          await refreshTaskList();
        } else {
          toast.error("Failed to clone task");
        }
      } catch (error) {
        toast.error("Error cloning task");
        console.error(error);
      }
    };

    const handleSync = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!taskId) return;

      try {
        const res = await taskManagerStore.refresh_user_tasks(taskId);
        if (res?.status === 200 || res?.status === 204) {
          toast.success("Task synced successfully");
          await refreshTaskList();
        } else {
          toast.error("Failed to sync task");
        }
      } catch (error) {
        toast.error("Error syncing task");
        console.error(error);
      }
    };

    const handleDelete = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!taskId) return;

      if (!window.confirm("Are you sure you want to delete this task?")) {
        return;
      }

      try {
        const res = await taskManagerStore.delete_task(taskId);
        if (res?.status === 200 || res?.status === 204) {
          toast.success("Task deleted successfully");
          await refreshTaskList();
        } else {
          toast.error("Failed to delete task");
        }
      } catch (error) {
        toast.error("Error deleting task");
        console.error(error);
      }
    };

    const handleShowMap = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!params.data) return;

      // Set active task, close task view if open, and show map
      taskManagerStore.set_active_task(params.data);
      await taskManagerStore.set_task_view_modal(false);
      await taskManagerStore.update_show_booth_map(true);
    };

    return (
      <div className="flex items-center justify-center gap-1 h-full">
        <IconButton
          size="medium"
          onClick={async (e) => {
            e.stopPropagation();
            taskManagerStore.set_active_task(params.data);
            taskManagerStore.set_task_view_modal(true);
            // Refresh list after viewing (in case data changed)
            await refreshTaskList();
          }}
          sx={{
            color: "#3B82F6",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "#EFF6FF",
            },
          }}
          title="View Task"
        >
          <i className="fas fa-eye text-sm" />
        </IconButton>

        <IconButton
          size="medium"
          onClick={async (e) => {
            e.stopPropagation();
            taskManagerStore.set_active_task(params.data);
            taskManagerStore.set_task_edit_modal(true);
            // Refresh list after editing
            await refreshTaskList();
          }}
          sx={{
            color: "#10B981",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "#D1FAE5",
            },
          }}
          title="Edit Task"
        >
          <i className="fas fa-edit text-sm" />
        </IconButton>

        <IconButton
          size="medium"
          onClick={handleClone}
          sx={{
            color: "#8B5CF6",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "#F3E8FF",
            },
          }}
          title="Clone Task"
        >
          <i className="fas fa-copy text-sm" />
        </IconButton>

        <IconButton
          size="medium"
          onClick={handleSync}
          sx={{
            color: "#F59E0B",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "#FEF3C7",
            },
          }}
          title="Sync Task"
        >
          <i className="fas fa-sync-alt text-sm" />
        </IconButton>

        <IconButton
          size="medium"
          onClick={handleDelete}
          sx={{
            color: "#EF4444",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "#FEE2E2",
            },
          }}
          title="Delete Task"
        >
          <i className="fas fa-trash text-sm" />
        </IconButton>

        <IconButton
          size="medium"
          onClick={handleShowMap}
          sx={{
            color: "#6366F1",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "#EEF2FF",
            },
          }}
          title="Show Map"
        >
          <i className="fas fa-map-marked-alt text-sm" />
        </IconButton>
      </div>
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Task Name",
        field: "task_name",
        sortable: true,
        width: 250,
        cellRenderer: (params) => (
          <div className="flex flex-col py-1">
            <span className="font-semibold text-sm text-gray-900 line-clamp-1">{params?.value}</span>
            <span className="text-xs text-gray-500">{params?.data?.region}</span>
          </div>
        ),
      },
      {
        headerName: "Assigned To",
        field: "assigned_to",
        sortable: true,
        width: 220,
        cellRenderer: (params) => {
          if (!params?.value?.name) {
            return (
              <div className="flex items-center h-full py-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Unassigned
                </span>
              </div>
            );
          }

          return (
            <AssignedBadge
              name={params.value?.name}
              phone={params.value?.phonenum}
            />
          );
        },
      },
      {
        headerName: "Interviews",
        field: "interviews_count",
        sortable: true,
        width: 100,
        cellRenderer: (params) => (
          <div className="flex items-center justify-center h-full">
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-blue-50 text-blue-700">
              {params?.value || 0}
            </div>
          </div>
        ),
      },
      {
        headerName: "Field Work",
        field: "field_work_progress",
        sortable: true,
        width: 100,
        cellRenderer: (params) => (
          <CircularProgress
            progress={safe_get(params.value, "progress_field_work", 0)}
            color="#10B981"
            size={50}
            strokeWidth={5}
          />
        ),
      },
      {
        headerName: "QC Check",
        field: "qc_progress",
        sortable: true,
        width: 100,
        cellRenderer: (params) => (
          <CircularProgress
            progress={safe_get(params.data.field_work_progress, "progress_qa", 0)}
            color="#F59E0B"
            size={50}
            strokeWidth={5}
          />
        ),
      },
      {
        headerName: "Valid | Invalid | Pending",
        field: "interview_status",
        sortable: false,
        width: 200,
        cellRenderer: (params) => {
          const validCount = safe_get(params.data.field_work_progress, "by_qastatus.valid", 0);
          const invalidCount = safe_get(params.data.field_work_progress, "by_qastatus.invalid", 0);
          const notCheckedCount = safe_get(params.data.field_work_progress, "by_qastatus.not-checked", 0);

          return (
            <div className="flex items-center justify-center gap-4 h-full">
              <div className="flex flex-col items-center">
                <i className="fas fa-circle-check text-green-500 text-base mb-1" />
                <span className="text-sm font-semibold text-gray-700">{validCount}</span>
              </div>
              <div className="flex flex-col items-center">
                <i className="fas fa-circle-xmark text-red-500 text-base mb-1" />
                <span className="text-sm font-semibold text-gray-700">{invalidCount}</span>
              </div>
              <div className="flex flex-col items-center">
                <i className="fas fa-circle-exclamation text-yellow-500 text-base mb-1" />
                <span className="text-sm font-semibold text-gray-700">{notCheckedCount}</span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 240,
        cellRenderer: actionButtons,
        suppressMovable: true,
        suppressMenu: true,
        sortable: false,
        cellClass: "flex items-center justify-center",
      },
    ],
    [projectid, userList, status, pivLevel1, tasklist_current_page_number]
  );
  return columnDefs;
};

export default Columns;
